import { Stack } from "@mui/material";
import { t } from "i18next";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CustomContainer } from "../../../../components/Containers";
import GlobalButton from "../../../../components/CustomButton";
import ProductTable from "../../../../components/Tables/ProductTable";
import useDebounce from "../../../../hooks/useDebounce";
import { setProductPageType } from "../../../../lib/slices/commonSlice";
import { setProductId } from "../../../../lib/supplierSlice/productIdSlice";
import {
  exportProducts,
  getAllSupplierProducts,
  updateSupplierProductStatus,
} from "../../../../services/supplier/productApiFn";
import {
  calculateProductFinalPrice,
  calculateVat,
  handleOpenSnackBar,
  roundTo2Decimal,
} from "../../../../utils";
import { Filters } from "./Filters";
import VariantsDialog from "./VariantDialog";
import AppLoader from "../../../../components/AppLoader";
import {
  setProductCurrentPage,
  setProductPageLimit,
  setProductTotalCount,
  setProductTotalPages,
} from "../../../../lib/slices/paginationSlice";
import { CSVLink } from "react-csv";
import moment from "moment";
import {
  CURRENCY,
  DEFAULT_VAT_PERCENTAGE,
  PRODUCT_USAGE_TYPE,
} from "../../../../constants/Variables";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

const headerData = [
  "common.createdAt",
  "common.name&Image",
  "common.sku",
  "common.category",
  "common.subCategory",
  "common.subSubCategory",
  "common.productPrice",
  "common.profitMargin",
  "common.discount",
  "common.finalPrice",
  "common.vat",
  "common.vatApplied",
  "common.finalPriceAfterVat",
  "common.initialQuantity",
  "common.quantity",
  "common.totalOrders",
  "common.rating",
  "common.variants",
  "common.status",
  // "common.actions",
];

const tableRows = [
  "created_date",
  "name_image",
  "product_sku",
  "category_name",
  "sub_category_name",
  "sub_sub_category_name",
  "product_price",
  "product_profit_margin",
  "product_discount",
  "product_final_price",
  "app_vat",
  "product_vat_amount",
  "product_final_price_after_vat",
  "product_initial_quantity",
  "product_quantity",
  "totalOrders",
  "product_rating",
  "product_variants",
  "product_status",
  "actions",
];

export const ProductList = ({
  handleChangeTab,
  fetchchedUploadedProducts,
  productsCount,
}) => {
  // Dispatch Redux
  const dispatch = useDispatch();
  // Open Variants Dialog re
  const openVariantsDialogRef = useRef(null);
  // CSV Link Ref
  const csvLinkRef = useRef(null);
  // Export Data State
  const [exportData, setExportData] = useState([]);
  // All Products State
  const [allProducts, setAllProducts] = useState([]);
  // Loading State
  const [isLoading, setIsLoading] = useState(false);
  // Export Loading State
  const [exportLoading, setExportLoading] = useState(false);
  // Get User Data from Redux
  const userData = useSelector((state) => state.userData.user);
  // Get Active Tab from Redux
  const activeTab = useSelector((state) => state.activeTab.tabs);
  // Get Current Language from Redux
  const { currentLanguage } = useSelector((state) => state.translationReducer);
  // Get Pagination from Redux
  const pagination = useSelector((state) => state.paginationReducer);
  // Get Product Search Page from Redux
  const searchKey = useSelector((state) => state.SearchKeysReducer.searchKeys);
  // Get Setting from the Redux
  const { settings } = useSelector((state) => state.settingsReducer);
  // Get Debounced Search Term
  const debouncedSearchTerm = useDebounce(
    searchKey.supplierProductSearchValue,
    1000,
    true,
    () => {
      dispatch(setProductCurrentPage(1));
    },
  );

  // Can Change Product Status
  // const canChangeStatus =
  //     userData?.canUpload <= productsCount.activeProductsCount;
  const canChangeStatus = userData?.canUpload <= 0;

  useEffect(() => {
    if (userData) {
      fetchchedAllProducts(
        debouncedSearchTerm,
        searchKey.supplierProductCategoryId,
        searchKey.supplierProductSubCategoryId,
        searchKey.supplierProductSubSubCategoryId,
        pagination.productPage,
        pagination.productLimit,
      );
    }
  }, [
    userData,
    debouncedSearchTerm,
    searchKey.supplierProductCategoryId,
    searchKey.supplierProductSubCategoryId,
    searchKey.supplierProductSubSubCategoryId,
    pagination.productPage,
    pagination.productLimit,
  ]);

  const fetchchedAllProducts = async (
    search,
    categoryId,
    subCategoryId,
    subSubCategoryId,
    page,
    limit,
  ) => {
    let query = {
      search: search || "",
      categoryId: categoryId === "All" ? "" : categoryId || "",
      subCategoryId: subCategoryId === "All" ? "" : subCategoryId || "",
      subSubCategoryId:
        subSubCategoryId === "All" ? "" : subSubCategoryId || "",
      page: page || 1,
      limit: limit || 20,
      supplierId: userData?._id,
    };
    try {
      setIsLoading(true);
      const response = await getAllSupplierProducts(query);
      if (response?.data?.status === "success") {
        const products = response?.data?.data?.data;
        const pagination = response?.data?.data?.pagination;
        // fetchchedUploadedProducts();
        setAllProducts(products);
        dispatch(setProductPageLimit(pagination.limit));
        dispatch(setProductCurrentPage(pagination.page));
        dispatch(setProductTotalCount(pagination.total));
        dispatch(setProductTotalPages(pagination.totalPages));
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeProductStatus = async (e, row) => {
    if (e && canChangeStatus) {
      return handleOpenSnackBar(
        "error",
        t("restoreLimitMessage", {
          canUploadCount: userData?.canUpload,
        }),
      );
    }

    try {
      setIsLoading(true);
      const response = await updateSupplierProductStatus(row?._id, {
        status: e,
      });
      if (response?.data?.status === "success") {
        fetchchedAllProducts(
          searchKey.supplierProductSearchValue,
          searchKey.supplierProductCategoryId,
          searchKey.supplierProductSubCategoryId,
          searchKey.supplierProductSubSubCategoryId,
          pagination.productPage,
          pagination.productLimit,
        );
        handleOpenSnackBar("success", response?.data?.message);
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditProdut = (row) => {
    handleChangeTab("add_edit_product");
    dispatch(setProductPageType("edit"));
    dispatch(setProductId(row?._id));
  };

  const handleOpenVariantsDialog = (row) => {
    openVariantsDialogRef.current?.openDialog({ row });
  };

  // Export data for products
  const prepareExportData = async () => {
    try {
      setExportLoading(true);
      const query = {
        supplierId: userData?._id,
      };
      const response = await exportProducts(query);
      if (response?.data?.status === "success") {
        const data = response?.data?.data;
        const formattedData = [];

        data.forEach((item) => {
          if (!item.isVariant || item.variants.length === 0) {
            const finalProductPrice = calculateProductFinalPrice(
              item.price,
              item.discount,
              item.profitMargin,
            );

            // This is calculated for both the product and the base packages
            const productVat = calculateVat(
              finalProductPrice,
              settings?.tax || DEFAULT_VAT_PERCENTAGE,
            );

            // This is calculated for both the product and the base packages
            const finalPriceAfterVat = roundTo2Decimal(
              finalProductPrice + productVat,
            );

            formattedData.push({
              "Created At": moment(item.createdAt).format("MM-DD-YYYY") || "-",
              "Updated At": moment(item.updatedAt).format("MM-DD-YYYY") || "-",
              "Object Id": item._id || "-",
              "Slug In (Ro)": item.mlSlug?.ro || "-",
              "Slug In (En)": item.mlSlug?.en || "-",
              "Store Name": item.createdBy.businessName || "-",
              "Cover Image": item.coverImage || "-",
              Images: item.images.map((image) => image).join(", "),
              "Name In (Ro)": item.name?.ro || "-",
              "Name In (En)": item.name?.en || "-",
              Brand: item.brand || "-",
              "Solution Type (Ro)":
                item.solutionType
                  ?.map(
                    (type) =>
                      PRODUCT_USAGE_TYPE.find((u) => u.value === type)?.label
                        ?.ro,
                  )
                  .filter(Boolean)
                  .join(", ") || "-",
              "Solution Type (En)":
                item.solutionType
                  ?.map(
                    (type) =>
                      PRODUCT_USAGE_TYPE.find((u) => u.value === type)?.label
                        ?.en,
                  )
                  .filter(Boolean)
                  .join(", ") || "-",
              "Category (Ro)": item.category?.name?.ro || "-",
              "Category (En)": item.category?.name?.en || "-",
              "Sub Category (Ro)":
                item.subCategory?.map((sub) => sub.name?.ro).join(", ") || "-",
              "Sub Category (En)":
                item.subCategory?.map((sub) => sub.name?.en).join(", ") || "-",
              "Sub Sub Category (Ro)":
                item.subSubCategory?.map((sub) => sub.name?.ro).join(", ") ||
                "-",
              "Sub Sub Category (En)":
                item.subSubCategory?.map((sub) => sub.name?.en).join(", ") ||
                "-",
              SKU: item.sku || "-",
              Price: `${item.price} ${CURRENCY}`,
              "Profit Margin": `${item.profitMargin}%`,
              Discount: `${item.discount} ${CURRENCY}`,
              "Final Price": `${item.finalPrice} ${CURRENCY}`,
              VAT: `${settings?.tax || DEFAULT_VAT_PERCENTAGE}%`,
              "VAT Amount": `${productVat} ${CURRENCY}`,
              "Price After VAT": `${finalPriceAfterVat} ${CURRENCY}`,
              "Initial Quantity": item.initialQuantity,
              Quantity: item.quantity,
              "Variant Attributes": "-",
              Featured: item.isFeatured ? "Yes" : "No",
              Status: item.status ? "Active" : "Inactive",
              "Total Orders": item.totalOrders,
              Rating: item.rating.toFixed(0),
            });
          } else {
            item.variants.forEach((variant) => {
              const attrString = Object.entries(variant.attributes)
                .map(([key, val]) => `${key}: ${val.value}`)
                .join(" | ");

              const finalProductPrice = calculateProductFinalPrice(
                variant.price,
                variant.discount,
                variant.profitMargin,
              );

              const productVat = calculateVat(
                finalProductPrice,
                settings?.tax || DEFAULT_VAT_PERCENTAGE,
              );

              const finalPriceAfterVat = roundTo2Decimal(
                finalProductPrice + productVat,
              );

              formattedData.push({
                "Created At":
                  moment(item.createdAt).format("MM-DD-YYYY") || "-",
                "Updated At":
                  moment(item.updatedAt).format("MM-DD-YYYY") || "-",
                "Object Id": item._id || "-",
                "Slug In (Ro)": item.mlSlug?.ro || "-",
                "Slug In (En)": item.mlSlug?.en || "-",
                "Store Name": item.createdBy.businessName || "-",
                "Cover Image": variant.images[0] || "-",
                Images: variant.images.map((image) => image).join(", "),
                "Name In (Ro)": item.name?.ro || "-",
                "Name In (En)": item.name?.en || "-",
                Brand: item.brand || "-",
                "Solution Type (Ro)":
                  item.solutionType
                    ?.map(
                      (type) =>
                        PRODUCT_USAGE_TYPE.find((u) => u.value === type)?.label
                          ?.ro,
                    )
                    .filter(Boolean)
                    .join(", ") || "-",
                "Solution Type (En)":
                  item.solutionType
                    ?.map(
                      (type) =>
                        PRODUCT_USAGE_TYPE.find((u) => u.value === type)?.label
                          ?.en,
                    )
                    .filter(Boolean)
                    .join(", ") || "-",
                "Category (Ro)": item.category?.name?.ro || "-",
                "Category (En)": item.category?.name?.en || "-",
                "Sub Category (Ro)":
                  item.subCategory?.map((sub) => sub.name?.ro).join(", ") ||
                  "-",
                "Sub Category (En)":
                  item.subCategory?.map((sub) => sub.name?.en).join(", ") ||
                  "-",
                "Sub Sub Category (Ro)":
                  item.subSubCategory?.map((sub) => sub.name?.ro).join(", ") ||
                  "-",
                "Sub Sub Category (En)":
                  item.subSubCategory?.map((sub) => sub.name?.en).join(", ") ||
                  "-",
                SKU: variant.sku || "-",
                Price: `${variant.price} ${CURRENCY}`,
                "Profit Margin": `${variant.profitMargin}%`,
                Discount: `${variant.discount} ${CURRENCY}`,
                "Final Price": `${variant.finalPrice} ${CURRENCY}`,
                VAT: `${settings?.tax || DEFAULT_VAT_PERCENTAGE}%`,
                "VAT Amount": `${productVat} ${CURRENCY}`,
                "Price After VAT": `${finalPriceAfterVat} ${CURRENCY}`,
                "Initial Quantity": variant.quantity,
                Quantity: variant.quantity,
                "Variant Attributes": attrString || "-",
                Featured: item.isFeatured ? "Yes" : "No",
                Status: item.status ? "Active" : "Inactive",
                "Total Orders": item.totalOrders,
                Rating: item.rating.toFixed(0),
              });
            });
          }
        });

        setExportData(formattedData);

        // Data set hone ke baad manually click trigger karo
        setTimeout(() => {
          csvLinkRef.current?.link?.click();
        }, 100);
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setExportLoading(false);
    }
  };

  const renderHeaderActions = () => {
    return (
      <Stack
        flexGrow={1}
        direction={{ md: "row" }}
        gap={1}
        justifyContent={"flex-end"}
        alignItems={"center"}
      >
        <GlobalButton
          variant={"contained"}
          btnText={t("supplierProductTab.addProduct")}
          handleClickBtn={() => {
            handleChangeTab("add_edit_product");
            dispatch(setProductPageType("add"));
            dispatch(setProductId(""));
          }}
          styleProps={{
            width: "auto",
            height: 40,
          }}
        />
        <CSVLink
          ref={csvLinkRef}
          data={exportData}
          filename={"products.csv"}
          className="csv-link"
          target="_blank"
          style={{ display: "none" }}
        />

        <GlobalButton
          variant={"export_button"}
          btnText={"export"}
          startIcon={<FileDownloadIcon width={20} />}
          disabled={exportLoading}
          handleClickBtn={prepareExportData}
        />
      </Stack>
    );
  };

  const handlePageChange = (event, newPage) => {
    const page = newPage + 1;
    dispatch(setProductCurrentPage(page));
  };

  const handleRowsPerPageChange = (event) => {
    const limit = parseInt(event.target.value, 10);
    dispatch(setProductPageLimit(limit));
    dispatch(setProductCurrentPage(1));
  };

  const renderTable = useMemo(() => {
    return (
      <ProductTable
        minWidth={3000}
        tableHeader={headerData}
        displayRows={tableRows}
        tableData={allProducts}
        showActions={true}
        handleChangeProductStatus={handleChangeProductStatus}
        showEdit={true}
        handleEdit={handleEditProdut}
        onOpenVariantsDialog={handleOpenVariantsDialog}
        paginatedData={{
          page: pagination.productPage,
          limit: pagination.productLimit,
          total: pagination.productTotal,
          totalPages: pagination.productTotalPages,
        }}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        isLoading={isLoading}
      />
    );
  }, [allProducts, isLoading]);

  return (
    <React.Fragment key="supplier-product-list">
      <AppLoader isVisible={isLoading} />
      <CustomContainer
        primaryHeading={t("supplierProductTab.allProducts")}
        secondaryHeading={t("supplierProductTab.productDetailsDescription")}
        customHeaderActions={renderHeaderActions()}
        filterSection={<Filters />}
      >
        {renderTable}

        <VariantsDialog ref={openVariantsDialogRef} />
      </CustomContainer>
    </React.Fragment>
  );
};
